import React, { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { Layout } from 'app/theme/layout'
import { Helmet } from 'react-helmet-async'
import { useQuery } from 'jsonapi-react'
import FoodTruckCatering from 'app/theme/sections/FoodTruckCatering'
import WhyWorkWithUs from 'app/theme/sections/WhyWorkWithUs'
import CateringForAnyEvent from 'app/theme/sections/CateringForAnyEvent'
import SaveTime from 'app/theme/sections/SaveTime'
import MakeEveryoneHappy from 'app/theme/sections/MakeEveryoneHappy'
import SitBackRelax from 'app/theme/sections/SitBackRelax'
import RepeatRepeat from 'app/theme/sections/RepeatRepeat'
import HowItWorks from 'app/theme/sections/HowItWorks'
import HowItWorksLarge from 'app/theme/sections/HowItWorksLarge'
import Calculator from 'app/theme/calculator'
import FeaturedClients from 'app/theme/sections/FeaturedClients'
import TagList from 'app/theme/sections/TagList'
import Slider from 'app/theme/slider'
import NotFoundPage from 'app/pages/NotFoundPage'
import ErrorPage from 'app/pages/ErrorPage'
import LeadModal from 'app/leads/LeadModal'
import { useModalState } from 'lib/modal'
import styles from './CityCateringPage.module.css'

export default function CityCateringPage(props) {
  const { openModal, ...modalState } = useModalState()

  const params = useParams()

  const query = useQuery(['places', {
    basic: true,
    filter: {
      slug: params.citySlug,
    },
  }])

  const catererQuery = useQuery(params.catererSlug && ['caterers', {
    filter: {
      slug: params.catererSlug,
      city_or_region_slug: params.citySlug,
    },
  }])

  if (query.isLoading || catererQuery.isLoading) {
    return null
  }

  if (!query.data || (params.catererSlug && !catererQuery.data)) {
    return <ErrorPage />
  }

  if (!query.data.length || (params.catererSlug && !catererQuery.data.length)) {
    return <NotFoundPage />
  }

  const place = query.data[0]
  const caterer = catererQuery.data ? catererQuery.data[0] : null

  const nameParts = []
  if (place.type === 'region') {
    nameParts.push(place.name, place.country?.code)
  } else if (place.country?.code === 'US') {
    nameParts.push(place.name, place.region?.code)
  } else {
    nameParts.push(place.name, place.country?.code)
  }

  const displayName = nameParts.filter(Boolean).join(', ')

  return (
    <Layout>
      <Helmet>
        <title>Food Truck Catering - {displayName} - Roaming Hunger</title>
        <meta name="description" content={`Book a food truck for your next ${place.name} event with Roaming Hunger. You give us the details, and we find the best food trucks in ${displayName} for you.`}></meta>
        <meta name="og:title" content={`Food Truck Catering - ${displayName}`}></meta>
      </Helmet>
      <LeadModal
        {...modalState}
      />
      <FoodTruckCatering
        title={`${place.name} Food Truck Catering`}
        description={caterer ? `Bring gourmet food trucks, like ${caterer.name}, to your next event with the click of a button. Check out other ${place.name} food truck options below or book now to get rolling.` : undefined}
        onButtonClick={openModal}
        jumpTarget="#save-time"
      />
      <TruckSlider
        place={place}
      />
      <TagList
        heading={`${place.name} Food Truck Catering Options`}
        baseUrl={place.type === 'region' ? `/food-trucks/${place.url_slug}` : `/catering/${place.url_slug}`}
      />
      <SaveTime
        id="save-time"
        jumpTarget="#make-everyone-happy"
        onButtonClick={openModal}
      />
      <MakeEveryoneHappy
        id="make-everyone-happy"
        jumpTarget="#sit-back-relax"
        onButtonClick={openModal}
      />
      <SitBackRelax
        id="sit-back-relax"
        jumpTarget="#why-work-with-us"
        onButtonClick={openModal}
      />
      <WhyWorkWithUs
        id="why-work-with-us"
        onButtonClick={openModal}
      />
      <RepeatRepeat
        id="repeat-repeat"
        jumpTarget="#catering-any-event"
        onButtonClick={openModal}
      />
      <CateringForAnyEvent
        id="catering-any-event"
      />
      <HowItWorks
        onButtonClick={openModal}
      />
      <HowItWorksLarge
        onButtonClick={openModal}
      />
      <Calculator
        onButtonClick={openModal}
      />
      {place.type === 'city' && (
        <FeaturedClients
          onButtonClick={openModal}
          city={place}
        />
      )}
    </Layout>
  )
}

function TruckSlider(props) {
  const { place } = props

  const query = useQuery(['caterers', {
    sort: '-ranking',
    filter: {
      [place.type === 'region' ? 'region_id' : 'city_id']: place.id
    },
    include: [
      'photos',
    ],
    page: {
      size: 16
    },
  }])

  if (query.isLoading || !query.data?.length) {
    return null
  }

  const caterers = query.data

  return (
    <div className="pt-16 pb-1">
      <h2 className="font-platform leading-none px-20 md:px-0 text-4xl md:text-5xl text-navy text-center mb-10">
        The Best {place.name} Food Truck Catering
      </h2>
      <Slider
        slidesPerRow={4}
        rows={2}
        slidesToScroll={1}
        arrows={false}
        dots={false}
        autoplay
        responsive={[
          {
            breakpoint: 768,
            settings: {
              dots: false,
              arrows: false,
              slidesPerRow: 3,
              slidesToScroll: 1,
            },
          },
          {
            breakpoint: 414,
            settings: {
              dots: false,
              arrows: false,
              slidesPerRow: 2,
              slidesToScroll: 1,
            },
          },
        ]}
      >
        {caterers.map(c => (
          <div key={c.id}>
            <div className={styles.truck_slide_item}>
              <a href={`/${c.slug}/`}>
                <div className={styles.black} />
              </a>
              {c.photos[0] && (
                <div
                  className={[styles.truck_slide_item_image, 'w-full h-full']}
                  style={{
                    backgroundImage: `url('${c.photos[0].url}')`
                  }}
                />
              )}
              <a href={`/${c.slug}/`} className="absolute">
                <span>{c.name}</span>
              </a>
            </div>
          </div>
        ))}
      </Slider>
    </div>
  )
}
